import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { getDashboard } from "@/infrastructure/analytics/analyticsService";
import type { DashboardData } from "@/infrastructure/analytics/analyticsService";
import { colors, typography, transitions } from "@/design-system/tokens";

function formatDate(d: Date): string {
  return d.toISOString().split("T")[0]!;
}

function generateDateRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const current = new Date(from + "T12:00:00");
  const end = new Date(to + "T12:00:00");
  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]!);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function formatLabel(date: string): string {
  const d = new Date(date + "T12:00:00");
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const now = new Date();
const DEFAULT_TO = formatDate(now);
const DEFAULT_FROM = formatDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30));

export const DashboardPage = memo(function DashboardPage() {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(DEFAULT_TO);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { setData(await getDashboard(from, to)); } catch { setData(null); }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.analytics.title}</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.analytics.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }} className="dashboard-filters">
          <label style={labelStyle}>{t.analytics.from}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={dateStyle} />
          <label style={labelStyle}>{t.analytics.to}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={dateStyle} />
        </div>
      </div>

      {loading && <LoadingState />}
      {!loading && !data && <EmptyState message={t.analytics.noData} />}
      {!loading && data && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <MovementsChart data={data.movementsByDay} from={from} to={to} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }} className="dashboard-grid">
            <TopProductsChart data={data.topProducts} />
            <OccupancyChart data={data.warehouseOccupancy} />
          </div>
          <StockCards data={data.stockByWarehouse} />
        </div>
      )}
    </div>
  );
});

const MovementsChart = memo(function MovementsChart({ data, from, to }: { data: DashboardData["movementsByDay"]; from: string; to: string }) {
  const { t } = useI18n();
  const [hovered, setHovered] = useState<number | null>(null);

  const allDays = generateDateRange(from, to);
  const dataMap = new Map(data.map(d => [d.date, d]));
  const chartData = allDays.map(date => ({
    date,
    inflows: dataMap.get(date)?.inflows ?? 0,
    outflows: dataMap.get(date)?.outflows ?? 0,
  }));

  const totalInflows = chartData.reduce((s, d) => s + d.inflows, 0);
  const totalOutflows = chartData.reduce((s, d) => s + d.outflows, 0);

  if (chartData.length === 0) return <ChartCard title={t.analytics.movementsTitle}><EmptyState message={t.analytics.noData} /></ChartCard>;

  const maxVal = Math.max(...chartData.flatMap(d => [d.inflows, d.outflows]), 1);
  const n = chartData.length;
  const svgW = 600;
  const svgH = 200;
  const padL = 0;
  const padR = 0;
  const padT = 10;
  const padB = 24;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const groupW = chartW / n;
  const barW = Math.max(groupW * 0.3, 2);
  const labelEvery = n > 25 ? 7 : n > 12 ? 3 : 1;

  return (
    <ChartCard title={t.analytics.movementsTitle}>
      <div style={{ position: "relative" }}>
        <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
          {[0, 0.5, 1].map(pct => (
            <line key={pct} x1={padL} x2={svgW - padR} y1={padT + chartH - pct * chartH} y2={padT + chartH - pct * chartH} stroke="#f2f2f2" strokeWidth="0.5" />
          ))}
          {chartData.map((d, i) => {
            const x = padL + i * groupW + groupW / 2;
            const inflowH = (d.inflows / maxVal) * chartH;
            const outflowH = (d.outflows / maxVal) * chartH;
            const isHov = hovered === i;
            return (
              <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                <rect x={x - groupW / 2} y={padT} width={groupW} height={chartH} fill="transparent" />
                <rect x={x - barW - 0.5} y={padT + chartH - inflowH} width={barW} height={inflowH} rx="1.5" fill="#16a34a" opacity={isHov ? 1 : 0.75} />
                <rect x={x + 0.5} y={padT + chartH - outflowH} width={barW} height={outflowH} rx="1.5" fill="#dc2626" opacity={isHov ? 1 : 0.75} />
                {i % labelEvery === 0 && (
                  <text x={x} y={svgH - 4} textAnchor="middle" style={{ fontSize: "7px", fill: "#bbb" }}>{formatLabel(d.date)}</text>
                )}
              </g>
            );
          })}
        </svg>
        {hovered !== null && chartData[hovered] && (
          <div style={{ position: "absolute", top: "12px", right: "12px", background: "#111", color: "#fff", fontFamily: typography.fontFamily, fontSize: "11px", padding: "8px 12px", borderRadius: "8px", pointerEvents: "none", lineHeight: 1.6, zIndex: 10 }}>
            <div style={{ fontWeight: typography.weight.bold }}>{new Date(chartData[hovered]!.date + "T12:00:00").toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}</div>
            <div><span style={{ color: "#4ade80" }}>●</span> {t.analytics.inflows}: {chartData[hovered]!.inflows.toLocaleString()}</div>
            <div><span style={{ color: "#f87171" }}>●</span> {t.analytics.outflows}: {chartData[hovered]!.outflows.toLocaleString()}</div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
        <Legend color="#16a34a" label={`${t.analytics.inflows} (${totalInflows.toLocaleString()})`} />
        <Legend color="#dc2626" label={`${t.analytics.outflows} (${totalOutflows.toLocaleString()})`} />
      </div>
    </ChartCard>
  );
});

const TopProductsChart = memo(function TopProductsChart({ data }: { data: DashboardData["topProducts"] }) {
  const { t } = useI18n();
  if (data.length === 0) return <ChartCard title={t.analytics.topProductsTitle}><EmptyState message={t.analytics.noData} /></ChartCard>;

  const maxVal = Math.max(...data.map(d => d.totalMovements), 1);

  return (
    <ChartCard title={t.analytics.topProductsTitle}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {data.slice(0, 10).map(item => (
          <div key={item.productId} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#666", width: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item.productName}</span>
            <div style={{ flex: 1, height: "16px", background: "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}>
              <div style={{ width: `${(item.totalMovements / maxVal) * 100}%`, height: "100%", background: colors.primary, borderRadius: "4px", transition: "width 0.4s" }} />
            </div>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#111", minWidth: "30px", textAlign: "right" }}>{item.totalMovements}</span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
});

const OccupancyChart = memo(function OccupancyChart({ data }: { data: DashboardData["warehouseOccupancy"] }) {
  const { t } = useI18n();
  if (data.length === 0) return <ChartCard title={t.analytics.occupancyTitle}><EmptyState message={t.analytics.noData} /></ChartCard>;

  return (
    <ChartCard title={t.analytics.occupancyTitle}>
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center" }}>
        {data.map(wh => {
          const r = 30;
          const circ = 2 * Math.PI * r;
          const filled = (wh.percentage / 100) * circ;
          return (
            <div key={wh.warehouseId} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
              <svg width="72" height="72" viewBox="0 0 72 72">
                <circle cx="36" cy="36" r={r} fill="none" stroke="#ebebeb" strokeWidth="7" />
                <circle cx="36" cy="36" r={r} fill="none" stroke={colors.primary} strokeWidth="7" strokeDasharray={`${filled} ${circ - filled}`} strokeDashoffset={circ / 4} strokeLinecap="round" />
              </svg>
              <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, color: "#111" }}>{wh.percentage}%</span>
              <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#888", maxWidth: "80px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wh.warehouseName}</span>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
});

const StockCards = memo(function StockCards({ data }: { data: DashboardData["stockByWarehouse"] }) {
  const { t } = useI18n();
  if (data.length === 0) return <ChartCard title={t.analytics.stockTitle}><EmptyState message={t.analytics.noData} /></ChartCard>;

  return (
    <ChartCard title={t.analytics.stockTitle}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {data.map(wh => (
          <div key={wh.warehouseId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#f9f9f9", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{wh.warehouseName}</span>
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{wh.totalUnits.toLocaleString()}</span>
                <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#aaa", display: "block" }}>{t.analytics.totalUnits}</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#555" }}>{wh.totalProducts}</span>
                <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#aaa", display: "block" }}>{t.analytics.totalProducts}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ChartCard>
  );
});

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e8e8e8", padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
      <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{title}</h3>
      {children}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <div style={{ width: "12px", height: "3px", borderRadius: "2px", background: color }} />
      <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#888" }}>{label}</span>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <div style={{ padding: "32px", textAlign: "center" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{message}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}

const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#888" };
const dateStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "8px 12px", outline: "none" };
